import { z } from 'zod';

/**
 * Support ticket types (MazeedPAY backlog §6/§7).
 *
 * Zod enums rather than TypeScript enums, per the project convention: they give
 * both runtime validation and inferred union types from one declaration.
 * Values mirror the offerings-service enums exactly — a drift here silently
 * breaks filtering, since the API rejects unknown values with a 422.
 */

export const SupportTicketCategorySchema = z.enum([
  'payment_already_made',
  'request_temporary_unlock',
  'payment_dispute',
  'technical_issue',
  'contact_support',
]);

export const SupportTicketStatusSchema = z.enum([
  'open',
  'in_progress',
  'awaiting_customer',
  'resolved',
  'closed',
]);

export const SupportTicketPrioritySchema = z.enum([
  'low',
  'normal',
  'high',
  'urgent',
]);

export const SupportCommentAuthorTypeSchema = z.enum([
  'customer',
  'operator',
  'system',
]);

export const SupportTicketEventTypeSchema = z.enum([
  'created',
  'status_changed',
  'priority_changed',
  'assigned',
  'unassigned',
  'comment_added',
  'temp_unlock_requested',
  'temp_unlock_approved',
  'temp_unlock_rejected',
]);

export const SupportCommentSchema = z.object({
  id: z.string(),
  authorType: SupportCommentAuthorTypeSchema,
  authorName: z.string().nullable(),
  body: z.string(),
  /** True = an internal note. NEVER render one as a customer-visible reply. */
  isInternal: z.boolean(),
  createdAt: z.string(),
});

export const SupportEventSchema = z.object({
  id: z.string(),
  eventType: SupportTicketEventTypeSchema,
  actorName: z.string().nullable(),
  fromValue: z.string().nullable(),
  toValue: z.string().nullable(),
  createdAt: z.string(),
});

/** The backlog §6 ticket, with every context field it mandates. */
export const SupportTicketSchema = z.object({
  id: z.string(),
  ticketNumber: z.string(),
  tenantId: z.string().nullable(),
  customerId: z.string().nullable(),
  contractId: z.string().nullable(),
  deviceUnitId: z.string().nullable(),
  branchId: z.string().nullable(),
  customerName: z.string().nullable(),
  msisdn: z.string().nullable(),
  imei: z.string().nullable(),
  deviceName: z.string().nullable(),
  deviceStatus: z.string().nullable(),
  category: SupportTicketCategorySchema,
  subject: z.string(),
  description: z.string().nullable(),
  status: SupportTicketStatusSchema,
  priority: SupportTicketPrioritySchema,
  assignedToUserId: z.string().nullable(),
  assignedToName: z.string().nullable(),
  firstResponseAt: z.string().nullable(),
  slaDueAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  closureReason: z.string().nullable(),
  adminNote: z.string().nullable(),
  createdAt: z.string(),
  lastModifiedAt: z.string(),
});

export const SupportTicketDetailSchema = SupportTicketSchema.extend({
  comments: z.array(SupportCommentSchema),
  events: z.array(SupportEventSchema),
});

export const SupportTicketStatsSchema = z.object({
  open: z.number(),
  inProgress: z.number(),
  awaitingCustomer: z.number(),
  unassigned: z.number(),
  urgent: z.number(),
  total: z.number(),
});

// ── Request schemas (derived from the base, per the project convention) ──────

export const UpdateSupportTicketSchema = SupportTicketSchema.pick({
  status: true,
  priority: true,
  adminNote: true,
  closureReason: true,
}).partial();

export const AssignSupportTicketSchema = z.object({
  assignedToUserId: z.string().nullable(),
  assignedToName: z.string().max(255).nullable(),
});

export const CreateAdminCommentSchema = z.object({
  body: z.string().min(1).max(5000),
  isInternal: z.boolean(),
});

export const SupportTicketFiltersSchema = z.object({
  category: SupportTicketCategorySchema.optional(),
  status: SupportTicketStatusSchema.optional(),
  priority: SupportTicketPrioritySchema.optional(),
  customerId: z.string().optional(),
  deviceUnitId: z.string().optional(),
  assignedToUserId: z.string().optional(),
  unassignedOnly: z.boolean().optional(),
  branchId: z.string().optional(),
  search: z.string().optional(),
  skip: z.number().optional(),
  limit: z.number().optional(),
});

export type SupportTicketCategory = z.infer<typeof SupportTicketCategorySchema>;
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;
export type SupportTicketPriority = z.infer<typeof SupportTicketPrioritySchema>;
export type SupportCommentAuthorType = z.infer<
  typeof SupportCommentAuthorTypeSchema
>;
export type SupportTicketEventType = z.infer<
  typeof SupportTicketEventTypeSchema
>;
export type SupportComment = z.infer<typeof SupportCommentSchema>;
export type SupportEvent = z.infer<typeof SupportEventSchema>;
export type SupportTicket = z.infer<typeof SupportTicketSchema>;
export type SupportTicketDetail = z.infer<typeof SupportTicketDetailSchema>;
export type SupportTicketStats = z.infer<typeof SupportTicketStatsSchema>;
export type UpdateSupportTicket = z.infer<typeof UpdateSupportTicketSchema>;
export type AssignSupportTicket = z.infer<typeof AssignSupportTicketSchema>;
export type CreateAdminComment = z.infer<typeof CreateAdminCommentSchema>;
export type SupportTicketFilters = z.infer<typeof SupportTicketFiltersSchema>;

// ── Display metadata ────────────────────────────────────────────────────────

export const CATEGORY_LABELS: Record<SupportTicketCategory, string> = {
  payment_already_made: 'Payment Already Made',
  request_temporary_unlock: 'Request Temporary Unlock',
  payment_dispute: 'Payment Dispute',
  technical_issue: 'Technical Issue',
  contact_support: 'Contact Support',
};

export const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  awaiting_customer: 'Awaiting Customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

export const PRIORITY_LABELS: Record<SupportTicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const EVENT_LABELS: Record<SupportTicketEventType, string> = {
  created: 'Ticket created',
  status_changed: 'Status changed',
  priority_changed: 'Priority changed',
  assigned: 'Assigned',
  unassigned: 'Unassigned',
  comment_added: 'Message added',
  temp_unlock_requested: 'Temporary unlock requested',
  temp_unlock_approved: 'Temporary unlock approved',
  temp_unlock_rejected: 'Temporary unlock declined',
};

/** Statuses that mean the ticket is no longer being worked. */
export const TERMINAL_STATUSES: readonly SupportTicketStatus[] = [
  'resolved',
  'closed',
] as const;

export function isTerminal(status: SupportTicketStatus): boolean {
  return TERMINAL_STATUSES.includes(status);
}
